// The product's name, and how it reads in an environment that is not
// production (g93). One image runs in every environment, so the name cannot be
// a build constant: each side learns which environment it is in at run time
// (the API from TRAYFLUX_ENVIRONMENT, the page from a cookie its server sets)
// and asks here what to call itself. Every tab title, mail subject, connector
// title and icon address takes its brand from this file, so a surface cannot
// stay on production's name by being forgotten.
//
// Pure, like everything under shared/.

/** The fixed set. A marker for any other environment is a new entry here. */
export const ENVIRONMENTS = ["production", "test"] as const;

export type Environment = (typeof ENVIRONMENTS)[number];

/**
 * Reads a setting. Unset and empty both mean production, which is what local
 * work and CI run as. Anything outside the set is `null`, for the caller to
 * refuse: a misspelt value must never pass quietly as production.
 */
export function parseEnvironment(value: string | undefined | null): Environment | null {
  if (value === undefined || value === null || value === "") return "production";
  return (ENVIRONMENTS as readonly string[]).includes(value) ? (value as Environment) : null;
}

/** The chip's text, and `null` where there is no chip. */
export function environmentLabel(environment: Environment): string | null {
  return environment === "test" ? "Test" : null;
}

/** "Trayflux", or "Trayflux Test": the installed app, the tab and the connector. */
export function brandName(environment: Environment): string {
  const label = environmentLabel(environment);
  return label === null ? "Trayflux" : `Trayflux ${label}`;
}

/** A mail's subject and heading: "[Test] " in front, and nothing in production. */
export function mailSubject(environment: Environment, subject: string): string {
  const label = environmentLabel(environment);
  return label === null ? subject : `[${label}] ${subject}`;
}

/**
 * Where an icon is served from. The test set sits at its own paths, so a
 * service worker never holds one environment's bytes under the other's entry.
 * `file` is a name under `public/icons/`, such as `pwa-512x512.png`.
 */
export function iconPath(environment: Environment, file: string): string {
  return environment === "test" ? `/icons/test/${file}` : `/icons/${file}`;
}

/**
 * The files that differ by environment, as `[requested, served on test]`.
 * The page's document names production's paths and is the same everywhere,
 * because the service worker keeps it. So the web server answers these four
 * with test's copies on test: the manifest and the icons a browser or a
 * phone fetches by itself, some of them from clients that run no script.
 * None of the eight paths may be in the service worker's precache. The
 * Caddyfile and the preview server both follow this table, and tests hold
 * them to it.
 */
export const ENVIRONMENT_FILES = [
  ["/manifest.webmanifest", "/manifest.test.webmanifest"],
  ["/favicon.ico", "/icons/test/favicon.ico"],
  ["/icons/favicon-32x32.png", "/icons/test/favicon-32x32.png"],
  ["/icons/apple-touch-icon.png", "/icons/test/apple-touch-icon.png"],
] as const;

/**
 * How the page learns its environment: a cookie the web server sets on test
 * and never in production. A cookie, because the page can read it before its
 * first paint, on its first visit and with no network, and production pays
 * no request for it. It says "test" and nothing about anyone.
 */
export const ENVIRONMENT_COOKIE = "trayflux_environment";

/** The cookie's whole `Set-Cookie` value on test. 400 days is the longest a browser keeps one. */
export const ENVIRONMENT_SET_COOKIE = `${ENVIRONMENT_COOKIE}=test; Path=/; Max-Age=34560000; SameSite=Lax; Secure`;

/** Reads `document.cookie`'s text. No cookie, or a value outside the set, is production. */
export function environmentFromCookie(cookies: string): Environment {
  for (const part of cookies.split(";")) {
    const at = part.indexOf("=");
    if (at !== -1 && part.slice(0, at).trim() === ENVIRONMENT_COOKIE) {
      return parseEnvironment(part.slice(at + 1).trim()) ?? "production";
    }
  }
  return "production";
}
